import type { ReactNode, TouchEventHandler } from 'react';
import { Component, createRef } from 'react';

import STYLES from './MobilePanel.module.scss';

type Props = {
  header: ReactNode;
  body: ReactNode;
  transitionTime: number;
  onTouchEnd?: Function;
};

const fullMarginTop = 30;
const headerHeight = 60;
const ctaHeight = 85;

class MobilePanel extends Component<Props> {
  startY: number;

  lastY: number;

  dragging: boolean;

  minHeight: number;

  maxHeight: number;

  normalHeight: number;

  originHeight: number;

  sectionEl: any;

  constructor(props: Props) {
    super(props);

    this.startY = 0;
    this.lastY = 0;
    this.dragging = false;
    this.sectionEl = createRef();
    this.minHeight = 0;
    this.normalHeight = 0;
    this.maxHeight = 0;
    this.originHeight = 0;
  }

  componentDidMount() {
    const windowHeight =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight;
    this.normalHeight = windowHeight * 0.6 - headerHeight - ctaHeight;
    this.maxHeight = windowHeight - fullMarginTop - headerHeight - ctaHeight;
    this.sectionEl.style.height = `${this.normalHeight}px`;
  }

  animateSectionHeight = (targetHeight: number) => {
    const { transitionTime } = this.props;
    this.sectionEl.style.transition = `height ${transitionTime / 1000}s ease`;
    this.sectionEl.style.height = `${targetHeight}px`;
    setTimeout(() => {
      if (this.sectionEl) {
        this.sectionEl.style.transition = '';
      }
    }, transitionTime);
  };

  touchStart: TouchEventHandler<HTMLDivElement> = (e) => {
    if (this.sectionEl) {
      this.originHeight = this.sectionEl.getBoundingClientRect().height;
      this.startY = e.targetTouches[0].clientY;
    }
  };

  touchMove: TouchEventHandler<HTMLDivElement> = (e) => {
    if (this.sectionEl) {
      const currentY = e.targetTouches[0].clientY;
      this.lastY = currentY;
      const deltaY = this.lastY - this.startY;
      const targetSectionHeight = this.originHeight - deltaY;
      if (targetSectionHeight > this.maxHeight) {
        return;
      }
      if (targetSectionHeight < this.minHeight) {
        return;
      }
      this.dragging = true;
      this.sectionEl.style.height = `${targetSectionHeight}px`;
    }
  };

  touchEnd = () => {
    if (this.sectionEl && this.dragging) {
      const { onTouchEnd = () => {} } = this.props;
      const sectionHeight = this.sectionEl.getBoundingClientRect().height;

      let targetHeight;
      if (sectionHeight > (this.normalHeight + this.maxHeight) / 2) {
        targetHeight = this.maxHeight;
        onTouchEnd(true);
      } else if (sectionHeight < (this.normalHeight + this.minHeight) / 2) {
        targetHeight = this.minHeight;
        onTouchEnd(false);
      } else {
        targetHeight = this.normalHeight;
        onTouchEnd(false);
      }

      this.animateSectionHeight(targetHeight);
      this.dragging = false;
    }
  };

  render() {
    const { body, header } = this.props;
    return (
      <section className={STYLES.MobilePanel__section}>
        <div
          data-testid="fullmap-mobile-layout"
          onTouchStart={this.touchStart}
          onTouchMove={this.touchMove}
          onTouchEnd={this.touchEnd}
        >
          <div className={STYLES.MobilePanel__dragBarContainer}>
            <div className={STYLES.MobilePanel__dragBar} />
          </div>
          {header}
        </div>
        <div
          className={STYLES.MobilePanel__panelBody}
          ref={(el) => {
            this.sectionEl = el;
          }}
        >
          {body}
        </div>
      </section>
    );
  }
}

export default MobilePanel;
